// CallSummary.tsx · v12.10.15 (#1628 request-type breakdown - "N read from words") · 2026-10-13 · VoiceSpectrum AI
// shared-ui/CallSummary.tsx · v12.10.13 (T78 How the day went to UI-PROTO-REPORTS-B1 v1.6 LOCKED + REPORT_STANDARD v1.0: #1572 Show button
//   with the white dot · #1579 #1566 FILTERS panel with the shared date control · #1580 actions Print · Export ▾ (Excel, PDF, HTML) ·
//   Share · Schedule, disabled until there is data · #1578 "No calls for …" · #1587 five cards · #1576 #1588 endings with flags,
//   % after the bar, totals per flag · reasons as a two-column list · grid by date (≤ 7 days) or by weekday + calls per day ·
//   #1581 #1583 print header / running header / footer · #1594 HTML export · #1555 "1 call" · #1593 Support Ops box) · 2026-10-10 · VoiceSpectrum AI
// shared-ui/CallSummary.tsx · v12.10.12 (#1546 T80, UI-PROTO-CALL-SUMMARY v1.1 LOCKED 10/10) · 2026-10-10 · VoiceSpectrum AI
// "How the day went" - the call STORY for a day, a week or any dates: the day in one picture, where the calls ended, what
// the callers who gave a reason wanted, calls by day and hour, what we noticed. Story, not analysis: no scores.
// ONE screen for two hosts (BDR-016): the practice app's Reports (its own calls, caller findings only) and Support Ops
// Call Analyzer › Practice view (several practices, every finding with its kind). Data comes in through props - the
// host fetches (only after Show); this file needs nothing but React. Counts only - no caller names or numbers anywhere.
// The numbers are counted by the API from the stored calls with one fixed rule - never by an AI at report time (#1592).
import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import { type DateScope } from './DateScope';
import {
  PrintFooter, PrintHeader, ReportDates, ReportDivider, ReportEmpty, ReportMenu, ShowButton, etTime, exportHtml, fileSafe, printReport,
  scopeKey, scopeWindow, useBeforePrint, type ReportWindow,
} from './ReportFrame';
import './callSummary.css';

export type { ReportWindow } from './ReportFrame';

export interface CsPicture { calls: number; dayCount: number; firstHour: number | null; lastHour: number | null; busiestHour: number | null; busiestHourCount: number;
  stretchFrom: number | null; stretchTo: number | null; stretchCount: number; quietestHour: number | null; quietestCount: number; spanish: number; spanishPct: number;
  medianSeconds: number; busiestDay: string | null; busiestDayCount: number;
  answered: number; voicemails: number; requestsTaken: number; textsSent: number; busiestWeekday: string | null; busiestWeekdayCount: number }
export type CsFlag = 'success' | 'watch' | 'failure';
/** v12.10.14 (#1614): split = the parts of the merged row (Support Ops only). */
export interface CsEnding { key: string; label: string; count: number; pct: number; flag: CsFlag; split?: CsEnding[] | null }
/** v12.10.14 (#1615): breakdown = the intents under a reason (Schedule · Change · Other). */
export interface CsTopic { name: string; count: number; isNew: boolean; breakdown?: { name: string; count: number }[] | null; /** v12.10.15 (#1628): calls whose type came from the caller's words */ fromWords?: number }
export interface CsGridRow { date: string; label: string; counts: number[]; total: number }
export interface CsDay { date: string; label: string; calls: number; weekend: boolean }
export interface CsSilentDay { date: string; label: string; calls: number; silent: number; pct: number }
export interface CsFinding { id: string; kind: 'caller' | 'setup' | 'bug' | 'watch'; text: string }
export interface CallSummaryData { tenants: string[]; timeZone: string; from: string; to: string; picture: CsPicture; endings: CsEnding[]; reasonCount: number;
  topics: CsTopic[]; gridHours: number[]; grid: CsGridRow[]; findings: CsFinding[]; preparedAt: string; topicsPending: boolean;
  gridMode: 'days' | 'weekday'; days: CsDay[] | null; capped: boolean; silentTrend: CsSilentDay[] | null }

export type CsAuditAction = 'print' | 'pdf' | 'html';

const hl = (h: number | null | undefined) => (h === null || h === undefined ? '-' : `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`);
/** "3 - 4 PM", "11 AM - 1 PM" */
const span = (a: number, b: number) => {
  const ha = hl(a), hb = hl(b);
  return ha.slice(-2) === hb.slice(-2) ? `${ha.slice(0, -3)} - ${hb}` : `${ha} - ${hb}`;
};
const dur = (s: number) => `${Math.floor(s / 60)} min ${s % 60} s`;
/** #1555: "1 call", "2 calls" */
export const n1 = (n: number, one: string, many?: string) => `${n} ${n === 1 ? one : many ?? one + 's'}`;
const pct = (n: number, t: number) => (t <= 0 ? 0 : Math.round((100 * n) / t));
const FLAG: Record<CsFlag, [string, string]> = { success: ['Success', 'cs-f-s'], watch: ['Watch', 'cs-f-w'], failure: ['Failure', 'cs-f-f'] };
const KIND: Record<string, [string, string]> = { setup: ['setup', 'cs-k-set'], bug: ['bug', 'cs-k-bug'], watch: ['watch', 'cs-k-watch'] };

/** The sheets an Excel export carries - the same numbers the screen shows. */
export function summarySheets(d: CallSummaryData): { name: string; columns: string[]; rows: (string | number)[][] }[] {
  const sheets = [
    { name: 'Where calls ended', columns: ['How the call ended', 'Calls', '%', 'Flag'], rows: d.endings.map((e) => [e.label, e.count, e.pct, FLAG[e.flag]?.[0] ?? e.flag]) },
    { name: 'What callers wanted', columns: ['Reason', 'Calls', '%', 'New'], rows: d.topics.map((t) => [t.name, t.count, pct(t.count, d.reasonCount), t.isNew ? 'new' : '']) },
    { name: d.gridMode === 'weekday' ? 'Calls by weekday and hour' : 'Calls by day and hour', columns: [d.gridMode === 'weekday' ? 'Weekday' : 'Day', ...d.gridHours.map((h) => hl(h)), 'Total'], rows: d.grid.map((r) => [r.label, ...r.counts, r.total]) },
    { name: 'What we noticed', columns: ['#', 'Kind', 'Finding'], rows: d.findings.map((f) => [f.id, f.kind, f.text]) },
  ];
  if (d.gridMode === 'weekday' && d.days?.length) sheets.splice(3, 0, { name: 'Calls per day', columns: ['Day', 'Calls'], rows: d.days.map((x) => [x.label, x.calls]) });
  return sheets;
}

export default function CallSummary(props: {
  data: CallSummaryData | undefined;
  loading?: boolean;
  error?: string | null;
  /** Support Ops only: the page title above the filters (the practice app's Reports page draws its own top). */
  title?: string;
  subtitle?: string;
  /** The practice the shown report is for - the print header's title. */
  practiceName: string;
  /** The report view words in the print header ("Analyze", "Support Ops · Practice view"). */
  viewLabel?: string;
  supportView?: boolean;
  /** What was shown (null = Show not pressed yet). The host fetches only for this window. */
  shown: ReportWindow | null;
  /** Show pressed: the host applies its own pending filters (practice) and fetches. */
  onShow: (w: ReportWindow) => void;
  /** Extra controls at the start of the FILTERS panel (Support Ops: the practice picker) and their pending state. */
  barStart?: ReactNode;
  extraKey?: string;
  onExcel?: () => Promise<void> | void;
  onShare?: (email: string) => Promise<void>;
  /** Every print, PDF and HTML export is written to the audit log (#1594). */
  onAudit?: (action: CsAuditAction) => void;
}) {
  const { data, shown } = props;
  const [scope, setScope] = useState<DateScope>({ kind: 'preset', value: 'Today' });
  const [shownKey, setShownKey] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const key = scopeKey(scope) + '|' + (props.extraKey ?? '');
  const dirty = shownKey === null || shownKey !== key;
  const ready = !!shown && !!data && !props.loading && !props.error;
  const hasData = ready && data!.picture.calls > 0;
  const run = useMemo(() => (data ? etTime(data.preparedAt) : ''), [data]);
  const running = shown ? `${props.practiceName} · How the day went · ${shown.period}` : '';
  const wrapClass = 'vsr-root cs-root';
  const spec = () => (hasData && cardRef.current ? { node: cardRef.current, wrapClass, running } : null);
  useBeforePrint(spec);

  function show() { setShownKey(key); setMsg(null); props.onShow(scopeWindow(scope)); }
  function doPrint(kind: 'print' | 'pdf') {
    const s = spec(); if (!s) return;
    props.onAudit?.(kind);
    printReport(s);
  }
  function doHtml() {
    if (!hasData || !cardRef.current || !shown) return;
    props.onAudit?.('html');
    exportHtml({ node: cardRef.current, wrapClass, title: `How the day went - ${props.practiceName} - ${shown.label}`,
      fileName: `How-the-day-went_${fileSafe(props.practiceName)}_${shown.from.slice(0, 10)}.html`, prefixes: ['.vsr-', '.cs-'] });
  }
  async function share() {
    if (!props.onShare || !email.trim()) return;
    setBusy(true); setMsg(null);
    try { await props.onShare(email.trim()); setMsg(`Sent to ${email.trim()}.`); setEmail(''); }
    catch (e) { setMsg((e as Error).message || 'Not sent.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="vsr-root cs-root" data-testid="call-summary">
      {props.title && <div className="vsr-hd"><div><h1>{props.title}</h1>{props.subtitle && <div className="vsr-sub">{props.subtitle}</div>}</div></div>}
      <div className="vsr-bar">
        {props.barStart}
        <ReportDates value={scope} onChange={setScope} />
        <ShowButton dirty={dirty} onClick={show} />
        <div className="vsr-acts">
          <button type="button" className="vsr-btn" disabled={!hasData} onClick={() => doPrint('print')}>Print</button>
          <ReportMenu label="Export ▾" disabled={!hasData}>
            {props.onExcel && <button type="button" role="menuitem" className="vsr-mi" onClick={() => { void props.onExcel?.(); }}>Excel (.xlsx)</button>}
            <button type="button" role="menuitem" className="vsr-mi" onClick={() => doPrint('pdf')}>PDF - Save as PDF from the print window</button>
            <button type="button" role="menuitem" className="vsr-mi" onClick={doHtml}>HTML - one page, opens anywhere</button>
            <div className="vsr-mh">Every export is written to the audit log.</div>
          </ReportMenu>
          {props.onShare && (
            <ReportMenu label="Share" disabled={!hasData} keepOpen wide>
              <div style={{ padding: '4px 6px' }}>
                <label className="vsr-lbl" htmlFor="cs-share-to">Email the summary to</label>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <input id="cs-share-to" type="email" value={email} placeholder="name@example.com" style={{ flex: 1 }} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void share(); }} />
                  <button type="button" className="vsr-btn pr" disabled={busy || !email.trim()} onClick={() => void share()}>Send</button>
                </div>
                <div className="vsr-note">The summary goes in the email body - counts and plain sentences only, no caller names or numbers.</div>
              </div>
            </ReportMenu>
          )}
          <button type="button" className="vsr-btn" disabled title="Not available yet">Schedule</button>
        </div>
      </div>
      {msg && <div className="vsr-note" role="status">{msg}</div>}

      <ReportDivider label={shown ? `How the day went · ${shown.label} · ${props.practiceName}` : 'nothing shown yet - pick the dates and press Show'} />
      <div className="vsr-card" ref={cardRef}>
        <PrintHeader practice={props.practiceName} report="How the day went" view={props.viewLabel ?? 'Analyze'} period={shown?.period ?? ''} run={run} />
        {!shown ? (
          <ReportEmpty big="Pick the dates and press Show">The report loads once you press Show. After that, change any filter and press Show again - the white dot on Show means the report is not up to date with the filters.</ReportEmpty>
        ) : props.loading ? <ReportEmpty big="Loading…" />
          : props.error ? <ReportEmpty big={props.error} />
          : !data || data.picture.calls === 0 ? (
            <ReportEmpty big={`No calls for ${shown.label}`}>Pick other dates with the date controls above.</ReportEmpty>
          ) : <Body d={data} supportView={!!props.supportView} practice={props.practiceName} />}
        <PrintFooter printed={run} />
      </div>
    </div>
  );
}

function Body({ d, supportView, practice }: { d: CallSummaryData; supportView: boolean; practice: string }) {
  const p = d.picture, T = p.calls;
  const flagTotal = (f: CsFlag) => d.endings.filter((e) => e.flag === f).reduce((a, e) => a + e.count, 0);
  const maxEnding = Math.max(1, ...d.endings.map((e) => e.count));
  const maxCell = Math.max(1, ...d.grid.flatMap((r) => r.counts));
  const shade = (v: number) => (v ? { background: `hsl(262 70% ${Math.round(95 - (v / maxCell) * 55)}%)`, color: v / maxCell > 0.55 ? '#fff' : '#4a5068' } : { background: '#f3f4f6', color: 'transparent' });
  const weekday = d.gridMode === 'weekday';
  const busy = p.dayCount <= 1
    ? (p.busiestHour !== null ? `${hl(p.busiestHour)} · ${p.busiestHourCount}` : '')
    : weekday ? (p.busiestWeekday ? `${p.busiestWeekday.slice(0, 3)} · ${p.busiestWeekdayCount}` : '')
      : (p.busiestDay ? `${p.busiestDay} · ${p.busiestDayCount}` : '');

  // "The day in one picture" - the prototype's sentence; the card numbers (answered, median, Spanish, requests) are not repeated
  const picture = p.dayCount <= 1
    ? <><b>{n1(T, 'call')}</b>{p.firstHour !== null && <> between <b>{hl(p.firstHour)} and {hl((p.lastHour ?? p.firstHour) + 1)}</b></>}.
      {p.busiestHour !== null && <> The busiest hour was <b>{hl(p.busiestHour)} with {n1(p.busiestHourCount, 'call')}</b></>}
      {p.stretchFrom !== null && p.stretchTo !== null && <>; the busiest stretch was <b>{span(p.stretchFrom, p.stretchTo)} ({n1(p.stretchCount, 'call')})</b></>}
      {p.quietestHour !== null && p.quietestHour !== p.busiestHour && <> and the quietest <b>{span(p.quietestHour, p.quietestHour + 1)} ({p.quietestCount})</b></>}.</>
    : <><b>{n1(T, 'call')}</b> over {p.dayCount} days.
      {weekday ? (p.busiestWeekday && <> Busiest weekday <b>{p.busiestWeekday}</b>,</>) : (p.busiestDay && <> Busiest day <b>{p.busiestDay} ({p.busiestDayCount})</b>,</>)}
      {p.busiestHour !== null && <> busiest hour <b>{hl(p.busiestHour)}</b></>}
      {p.quietestHour !== null && p.quietestHour !== p.busiestHour && <>, quietest <b>{hl(p.quietestHour)}</b></>}.</>;

  return (
    <>
      <div className="vsr-sec">
        <h2 style={{ marginTop: 4 }}>The day in one picture</h2>
        <div className="cs-cards">
          <div className="cs-cd"><div className="n">{T}</div><div className="l">{T === 1 ? 'call' : 'calls'}{busy && <> · busiest {busy}</>}</div></div>
          <div className="cs-cd"><div className="n">{p.answered} · {p.voicemails}</div><div className="l">answered · {p.voicemails === 1 ? 'voicemail' : 'voicemails'}</div></div>
          <div className="cs-cd"><div className="n">{dur(p.medianSeconds)}</div><div className="l">median call</div></div>
          <div className="cs-cd"><div className="n">{p.spanish} · {p.spanishPct}%</div><div className="l">Spanish {p.spanish === 1 ? 'call' : 'calls'}</div></div>
          <div className="cs-cd"><div className="n">{p.requestsTaken} · {p.textsSent}</div><div className="l">{p.requestsTaken === 1 ? 'request' : 'requests'} taken · {p.textsSent === 1 ? 'text' : 'texts'} sent</div></div>
        </div>
        <div className="cs-pic"><p>{picture}</p></div>
        {d.capped && <p className="vsr-note">Only the first 6,000 calls of these dates are counted - pick fewer dates for the full count.</p>}
      </div>

      <div className="vsr-sec">
        <h2>Where the {n1(T, 'call')} ended</h2>
        <table className="cs-ends">
          <thead><tr><th>How the call ended</th><th className="n">Calls</th><th className="b" aria-label="Share" /><th>Flag</th></tr></thead>
          <tbody>{d.endings.map((e) => (
            <Fragment key={e.key}>
              {/* v12.10.14 (#1614, UI-PROTO-REPORTS-B1 v1.9): a 3 px left edge in the row's flag colour */}
              <tr className={`cs-e-${e.flag}${supportView && e.split ? ' cs-has-split' : ''}`}>
                <td>{e.label}</td><td className="n"><b>{e.count}</b></td>
                <td className="b"><span className="cs-bw"><span className="cs-mb" style={{ width: `${Math.max(2, Math.round((e.count / maxEnding) * 86))}%` }} /><span className="cs-bp">{e.pct}%</span></span></td>
                <td><span className={`cs-flag ${FLAG[e.flag]?.[1] ?? 'cs-f-w'}`}>{FLAG[e.flag]?.[0] ?? e.flag}</span></td>
              </tr>
              {/* v12.10.14 (S229): Support Ops sees where the merged line hung up - identity · request · confirmation */}
              {supportView && e.split && e.split.length > 0 && (
                <tr className={`cs-e-${e.flag} cs-split`}><td colSpan={4}>{e.split.map((x) => `${x.label} ${x.count}`).join(' · ')} <span className="cs-ops">Support Ops only</span></td></tr>
              )}
            </Fragment>
          ))}</tbody>
        </table>
        <div className="cs-tot">
          {(['success', 'watch', 'failure'] as CsFlag[]).map((f) => { const n = flagTotal(f); return <span key={f}><span className={`cs-flag ${FLAG[f][1]}`}>{FLAG[f][0]}</span> <b>{n}</b> · {pct(n, T)}%</span>; })}
          <span className="cs-tot-all"><b>{T}</b> {T === 1 ? 'call' : 'calls'}</span>
        </div>
        <div className="cs-foot">Every call has exactly one ending, so the rows add up to the calls. The same dates always give the same numbers - endings are counted from the stored call with one fixed rule, never by an AI at report time. Asked for a Human: only the outcomes this practice has - no staff line, so no handoff row.</div>
      </div>

      <div className="vsr-sec">
        <h2>What the {n1(d.reasonCount, 'caller')} who gave a reason wanted</h2>
        {d.topics.length === 0 ? <div className="cs-none">No caller gave a reason in these dates.</div> : (
          <ul className="cs-wv">{d.topics.map((t) => (
            <li key={t.name}><span className="t">{t.name}{t.isNew && <span className="cs-new">new</span>}</span><span className="n">{t.count}</span><span className="p">{pct(t.count, d.reasonCount)}%</span>
              {/* v12.10.14 (#1615): the intent breakdown on an indented line under its reason */}
              {t.breakdown && t.breakdown.length > 0 && <span className="cs-brk">{t.breakdown.map((b) => `${b.name} ${b.count}`).join(' · ')}{(t.fromWords ?? 0) > 0 && <span className="cs-brk-words" title="The call was never sorted - its type was read from the caller's words or the voicemail"> · {t.fromWords} read from words</span>}</span>}</li>
          ))}</ul>
        )}
        {d.topicsPending && <p className="vsr-note vsr-noprint">Some calls are still being sorted into a reason - they show as &ldquo;Something else&rdquo; until then.</p>}
      </div>

      <div className="vsr-sec">
        <h2>{weekday ? 'Calls by weekday and hour' : 'Calls by day and hour'}</h2>
        {weekday && <div className="cs-foot" style={{ margin: '-4px 0 8px' }}>More than 7 days: one row per weekday (all the Mondays added up, and so on), and calls per day across the whole range below.</div>}
        <div className="cs-grid" style={{ gridTemplateColumns: `var(--cs-gl, 60px) repeat(${d.gridHours.length}, minmax(0, 1fr))` }}>
          <span />
          {d.gridHours.map((h) => <span key={h} className="h">{hl(h)}</span>)}
          {d.grid.map((r) => (
            <Fragment key={r.date}>
              <span className="d">{r.label}</span>
              {r.counts.map((v, i) => <span key={i} className="c" style={shade(v)} title={`${r.label} ${hl(d.gridHours[i])}: ${n1(v, 'call')}`}>{v || ''}</span>)}
            </Fragment>
          ))}
        </div>
        {weekday && d.days && d.days.length > 0 && <DayStrip days={d.days} />}
      </div>

      <div className="vsr-sec">
        <h2>What we noticed</h2>
        {d.findings.length === 0 ? <div className="cs-none">Nothing out of the ordinary.</div> : (
          <ul className="cs-fl">{d.findings.map((f) => (
            <li key={f.id}><b>{f.id}</b> {f.text}
              {supportView && f.kind !== 'caller' && KIND[f.kind] && <span className={`cs-kind ${KIND[f.kind][1]}`}>{KIND[f.kind][0]} · Support Ops only</span>}
            </li>
          ))}</ul>
        )}
      </div>

      {supportView && (
        <div className="cs-ops vsr-sec">
          <h2>Support Ops only</h2>
          <div className="cs-foot" style={{ marginTop: 0 }}>Not shown to the practice. Our own engine and setup, said plainly - the findings marked above, and the trend below.</div>
          {d.silentTrend && d.silentTrend.length > 1 ? (
            <>
              <h3>Silent hang-ups at the greeting - share of calls by day</h3>
              <div className="cs-trend">{d.silentTrend.map((x) => (
                <div key={x.date} style={{ height: `${Math.max(4, Math.min(100, x.pct * 2.6))}%` }} title={`${x.label}: ${x.silent} of ${n1(x.calls, 'call')}`}><span>{x.pct}%</span></div>
              ))}</div>
              <div className="cs-tl">{d.silentTrend.map((x) => <span key={x.date}>{d.silentTrend!.length > 10 ? x.label.split(' ')[1] : x.label}</span>)}</div>
            </>
          ) : <div className="cs-none">Pick more than one day to see the silent hang-up trend.</div>}
        </div>
      )}

      <p className="cs-foot vsr-noprint">Prepared {etTime(d.preparedAt)} · {practice} · counts only - open a call in Messages for the details.</p>
    </>
  );
}

function DayStrip({ days }: { days: CsDay[] }) {
  const max = Math.max(1, ...days.map((x) => x.calls));
  return (
    <>
      <div className="cs-days">{days.map((x) => (
        <div key={x.date} className={x.calls ? '' : 'we'} style={{ height: `${Math.max(3, (x.calls / max) * 100)}%` }} title={`${x.label}: ${n1(x.calls, 'call')}`} />
      ))}</div>
      <div className="cs-dl"><span>{days[0].label}</span><span>calls per day</span><span>{days[days.length - 1].label}</span></div>
    </>
  );
}
